import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightDocumentUpload from '../../assets/landing_page_images/Light-DocumentUploadFlow.webp';
import DarkDocumentUpload from '../../assets/landing_page_images/Dark-DocumentUploadFlow.webp';
import LightChat from '../../assets/landing_page_images/Light-Chat.webp';
import DarkChat from '../../assets/landing_page_images/Dark-Chat.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import './WHSComplianceAuditSoftware.css';

const RECORD_FEATURES = [
  {
    title: 'Audits and Inspections',
    icon: 'audit',
    paragraphs: [
      'Use digital inspections and audits to capture findings and create follow-up tasks. Audit findings can be connected to corrective actions, owners, evidence and completion status.',
    ],
  },
  {
    title: 'WHS Documents and Policies',
    icon: 'folder',
    paragraphs: [
      'Store policies, procedures, SWMS, risk assessments, WHS plans, registers and other compliance records in a searchable document library. Documents can include versions, review dates and expiry dates, helping teams keep track of controlled documentation.',
    ],
  },
  {
    title: 'Digital WHS Forms',
    icon: 'form',
    paragraphs: [
      'Create digital forms for workplace processes such as site inductions, toolbox talks, pre-start checks, hazard reports, incident reports and inspections. Workers can access forms through mobile devices or QR codes, while completed records can be exported as PDFs.',
    ],
  },
  {
    title: 'Corrective Actions',
    icon: 'task',
    paragraphs: [
      'Assign actions to specific people, set priorities and statuses, add comments or attachments, and track work through completion. This creates a structured workflow for addressing compliance gaps identified during audits or inspections.',
    ],
  },
  {
    title: 'Sites, Assets and Workers',
    icon: 'sites',
    paragraphs: [
      'Maintain site records, site-specific documents, digital form submissions and registers for vehicles, plant, equipment and tools. Employee profiles can also contain documents and submitted forms with role-based access.',
    ],
  },
];

const AUDIT_STEPS = [
  {
    title: 'Set up your compliance information',
    icon: 'folder',
    text: 'Add relevant documents, policies, procedures and registers.',
  },
  {
    title: 'Create an audit or compliance project',
    icon: 'project',
    text: 'Organise the audit within a project.',
  },
  {
    title: 'Record findings',
    icon: 'search',
    text: 'Capture issues, observations and supporting information.',
  },
  {
    title: 'Assign corrective actions',
    icon: 'task',
    text: 'Give each action an owner, priority and status.',
  },
  {
    title: 'Attach evidence',
    icon: 'evidence',
    text: 'Add relevant documents, comments or other supporting information.',
  },
  {
    title: 'Track completion',
    icon: 'complete',
    text: 'Monitor outstanding tasks until the required action has been completed.',
  },
];

const SPEC_TABLE = [
  { capability: 'WHS audits', value: 'Supported' },
  { capability: 'Inspections', value: 'Supported' },
  { capability: 'Corrective actions', value: 'Supported' },
  { capability: 'Digital WHS forms', value: 'Supported' },
  { capability: 'QR code forms', value: 'Supported' },
  { capability: 'WHS document management', value: 'Supported' },
  { capability: 'SWMS and risk assessments', value: 'Supported' },
  { capability: 'Incident reporting', value: 'Supported' },
  { capability: 'Site management', value: 'Supported' },
  { capability: 'Plant and asset registers', value: 'Supported' },
  { capability: 'Contractor/worker compliance', value: 'Supported' },
  { capability: 'AI compliance assistant', value: 'Supported' },
  { capability: 'Mobile access', value: 'Android and iOS apps' },
  { capability: 'Free trial', value: 'Available without card details' },
];

const FAQ_ITEMS = [
  {
    question: 'What is WHS audit software?',
    answer:
      'WHS audit software helps businesses plan, record and manage workplace health and safety audits, findings, evidence and follow-up actions. Complimate connects audit workflows with tasks and corrective actions.',
  },
  {
    question: 'Can Complimate help with WHS Act compliance?',
    answer:
      'Yes. Complimate can help businesses manage and document WHS obligations, including using its AI Compliance Assistant to reference uploaded company information alongside WHS legislation and government requirements. It does not itself guarantee legal compliance.',
  },
  {
    question: 'Can I manage WHS documents in Complimate?',
    answer:
      'Yes. The document management functionality supports policies, procedures, SWMS, risk assessments, WHS plans, registers and compliance records, with version and expiry tracking.',
  },
  {
    question: 'Can workers complete WHS forms digitally?',
    answer:
      'Yes. Workers can access active digital forms through mobile devices and QR codes, capture signatures and submit completed records.',
  },
  {
    question: 'Does Complimate replace a WHS consultant?',
    answer:
      'No. Complimate is a software platform for managing WHS and compliance activities. It should not be positioned as a replacement for professional WHS or legal advice.',
  },
  {
    question: 'Is Complimate available across Australia?',
    answer:
      'Yes. Complimate is positioned as an Australia-wide platform, while the SEO strategy also identifies NSW locations for additional local visibility.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/whs-compliance-audit-software/');

const SPEC_ICONS = [
  'audit',
  'clipboard',
  'task',
  'form',
  'qr',
  'folder',
  'shield',
  'hazard',
  'pin',
  'plant',
  'users',
  'spark',
  'mobile',
  'gift',
];

const ICON_PATHS = {
  audit: (
    <>
      <path d="M8 4h8M9 3v3h6V3M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  clipboard: (
    <>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M8 5V3h8v2M8 10h8M8 14h8M8 18h5" />
    </>
  ),
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  form: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  task: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8 12 2.4 2.4L16 9" />
    </>
  ),
  sites: (
    <>
      <path d="M4 21V8l8-5 8 5v13M9 21v-6h6v6" />
      <path d="M8 10h.01M12 10h.01M16 10h.01" />
    </>
  ),
  project: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 2v4M16 2v4M3 9h18M7 13h4M7 17h8" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </>
  ),
  evidence: (
    <>
      <path d="M6 3h8l4 4v14H6V3ZM14 3v5h5" />
      <path d="m9 15 2 2 4-5" />
    </>
  ),
  complete: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 2.5 2.5L16 9" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />,
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h4v4h-4zM20 14v3M14 20h3" />
    </>
  ),
  hazard: (
    <>
      <path d="M12 3 22 20H2L12 3Z" />
      <path d="M12 9v5M12 17h.01" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  plant: (
    <>
      <path d="M3 16h12v-5H8.5L6 8H3v8Z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="13" cy="18" r="2" />
      <path d="M15 13h3.5L21 16v2h-6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  spark: (
    <>
      <path d="m12 3 1.3 4.2L17.5 9l-4.2 1.8L12 15l-1.3-4.2L6.5 9l4.2-1.8L12 3Z" />
      <path d="m19 15 .6 1.9 1.9.6-1.9.6L19 21l-.6-1.9-1.9-.6 1.9-.6L19 15Z" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  gift: (
    <>
      <rect x="3" y="9" width="18" height="12" rx="2" />
      <path d="M12 9v12M2 9h20V5H2v4ZM12 5c-1-3-5-3-5-1 0 1.3 2 1 5 1ZM12 5c1-3 5-3 5-1 0 1.3-2 1-5 1Z" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 8h.01" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="wca-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.audit}
      </svg>
    </span>
  );
}

function WHSComplianceAuditSoftware() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="wca-page">
        <section className="wca-hero">
          <Container>
            <div className="wca-hero-grid">
              <div className="wca-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="audit" />
                  WHS Compliance &amp; Audit Software
                </p>
                <h1 className="seo-h1">WHS Compliance &amp; Audit Software</h1>
                <p className="seo-lead">
                  Complimate is WHS compliance and audit software for Australian
                  businesses that need a practical way to manage workplace health
                  and safety requirements. It brings audits, inspections, WHS
                  documents, digital forms, incidents, corrective actions and
                  compliance tasks into one platform.
                </p>
                <p className="seo-body">
                  Designed for WHS managers, compliance teams, site supervisors and
                  operations teams, Complimate helps businesses organise evidence,
                  identify compliance gaps and track actions through to completion.
                  It supports WHS management systems without claiming to replace
                  professional WHS or legal advice.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to="/contact-us"
                    className="btn-teal wca-primary-button"
                    id="whs_audit_book_demo_hero"
                  >
                    Book a Demo
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>
              <div className="wca-hero-visual">
                <div className="wca-visual-glow" aria-hidden="true" />
                <div className="wca-product-window">
                  <div className="wca-window-bar" aria-hidden="true">
                    <span /><span /><span />
                  </div>
                  <img
                    src={isDark ? DarkDashboard : LightDashboard}
                    alt="Complimate WHS compliance dashboard for audits, tasks and documents"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>
                <div className="wca-float-card wca-float-card-top" aria-hidden="true">
                  <FeatureIcon name="search" />
                  <span>Audit findings</span>
                </div>
                <div className="wca-float-card wca-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="complete" />
                  <span>Actions completed</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section wca-intro-section">
          <Container>
            <div className="wca-intro-grid">
              <div>
                <div className="wca-section-marker"><FeatureIcon name="audit" /></div>
                <h2 className="seo-h2">
                  What Is WHS Compliance and Audit Software?
                </h2>
                <p className="seo-body">
                  WHS compliance and audit software helps businesses organise the
                  processes and records used to manage workplace health and safety
                  obligations.
                </p>
                <p className="seo-body">
                  Instead of managing audit findings, policies, inspections, forms
                  and corrective actions across spreadsheets, email and separate
                  folders, Complimate connects these activities in one system.
                </p>
                <p className="seo-body">
                  For example, a WHS manager can run an internal audit, record a
                  finding, assign a corrective action, attach supporting evidence
                  and track the task until it is closed. Complimate&apos;s project
                  and task functionality supports this audit-to-action workflow.
                </p>
              </div>
              <div className="wca-shot-frame">
                <img
                  src={isDark ? DarkDocument : LightDocument}
                  alt="Complimate document library for WHS policies and audit evidence"
                  width="960"
                  height="680"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section wca-act-section">
          <Container>
            <div className="wca-act-grid">
              <div className="wca-shot-frame">
                <img
                  src={isDark ? DarkChat : LightChat}
                  alt="Complimate AI Compliance Assistant referencing WHS requirements"
                  width="960"
                  height="680"
                />
              </div>
              <div>
                <div className="wca-section-marker"><FeatureIcon name="shield" /></div>
                <h2 className="seo-h2">
                  How Does Complimate Support WHS Act Compliance?
                </h2>
                <p className="seo-body">
                  WHS Act compliance involves understanding and managing the duties
                  and obligations that apply to your organisation. The specific
                  requirements can depend on your Australian jurisdiction, industry
                  and circumstances.
                </p>
                <p className="seo-body">
                  Complimate is designed to help businesses identify, manage and
                  document WHS obligations rather than claim that software itself
                  makes a business legally compliant. This distinction is important
                  when using technology for workplace safety management.
                </p>
                <p className="seo-body">
                  The platform&apos;s AI Compliance Assistant can answer questions
                  using uploaded company documents and can cross-reference guidance
                  with WHS legislation, ISO standards and government requirements.
                  Users can also use it to support policy, procedure, risk
                  assessment and SWMS drafting.
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section wca-records-section">
          <Container>
            <div className="wca-records-intro">
              <div>
                <div className="wca-section-marker"><FeatureIcon name="clipboard" /></div>
                <h2 className="seo-h2">
                  How Can You Manage WHS Regulations and Compliance Records?
                </h2>
                <p className="seo-body">
                  Complimate provides several tools that work together as part of a
                  WHS compliance system:
                </p>
              </div>
              <div className="wca-shot-frame">
                <img
                  src={isDark ? DarkDocumentUpload : LightDocumentUpload}
                  alt="Complimate digital compliance records and WHS form workflow"
                  width="960"
                  height="680"
                />
              </div>
            </div>
            <div className="wca-record-grid">
              {RECORD_FEATURES.map((item, index) => (
                <article className="wca-record-card" key={item.title}>
                  <div className="wca-card-topline">
                    <FeatureIcon name={item.icon} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  {item.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 48)} className="seo-body">
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section wca-cta-section">
          <Container>
            <div className="wca-cta-card">
              <span className="wca-cta-orb wca-cta-orb-one" aria-hidden="true" />
              <span className="wca-cta-orb wca-cta-orb-two" aria-hidden="true" />
              <div className="wca-cta-copy">
                <h2 className="seo-h2">Manage WHS Compliance in One Place</h2>
                <p className="seo-body">
                  Bring WHS audits, documents, inspections, digital forms,
                  corrective actions and compliance tasks together with Complimate.
                </p>
              </div>
              <Button
                as={Link}
                to="/contact-us"
                className="btn-teal wca-cta-button"
                id="whs_audit_book_demo_mid"
              >
                Book a Demo
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section wca-workflow-section">
          <Container>
            <div className="wca-section-intro">
              <h2 className="seo-h2">How Does WHS Audit Software Work?</h2>
              <p className="seo-body">
                A typical WHS audit workflow in Complimate can follow these steps:
              </p>
            </div>
            <div className="wca-steps">
              {AUDIT_STEPS.map((step, index) => (
                <article key={step.title} className="wca-step">
                  <div className="wca-step-number">{index + 1}</div>
                  <FeatureIcon name={step.icon} />
                  <div>
                    <h3 className="seo-h4">
                      {index + 1}. {step.title}
                    </h3>
                    <p className="seo-body mb-0">{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="wca-note">
              <FeatureIcon name="complete" />
              <p className="seo-body mb-0">
                This approach connects the audit process with the work required
                after the audit instead of treating the audit report as the end of
                the process.
              </p>
            </div>
          </Container>
        </section>

        <section className="seo-section wca-spec-section">
          <Container>
            <div className="wca-section-intro">
              <h2 className="seo-h2">
                WHS Compliance &amp; Audit Software: Key Specifications
              </h2>
            </div>
            <div className="seo-table-wrap wca-spec-table">
              <table className="seo-table">
                <thead>
                  <tr>
                    <th scope="col">Capability</th>
                    <th scope="col">Complimate</th>
                  </tr>
                </thead>
                <tbody>
                  {SPEC_TABLE.map((row, index) => (
                    <tr key={row.capability}>
                      <th scope="row">
                        <span className="wca-table-feature">
                          <FeatureIcon name={SPEC_ICONS[index]} />
                          {row.capability}
                        </span>
                      </th>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="seo-body seo-diff-note">
              The product information confirms these capabilities as available
              areas for current SEO promotion; features still in development
              should not be presented as live functionality.
            </p>
          </Container>
        </section>

        <section className="seo-section wca-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default WHSComplianceAuditSoftware;
